"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createEndpoints = createEndpoints;
/**
 * All resource namespaces bound to a given axios instance. Shared by every
 * app — feature-specific endpoints for a new vertical (e.g. ride booking)
 * should be added as new namespaces here, not forked per app.
 */
function createEndpoints(apiClient) {
    const authApi = {
        login: (email, password) => apiClient.post("/auth/login", { email, password }),
        sendCode: (email) => apiClient.post("/auth/send-code", { email }),
        verifyCode: (email, code) => apiClient.post("/auth/verify-code", { email, code }),
        resendCode: (email) => apiClient.post("/auth/resend-code", { email }),
        register: (payload) => apiClient.post("/auth/register", payload),
        forgotPassword: (email) => apiClient.post("/auth/forgot-password", { email }),
        resetPassword: (token, password) => apiClient.post("/auth/reset-password", { token, password }),
        logout: () => apiClient.post("/auth/logout"),
    };
    const usersApi = {
        getMe: () => apiClient.get("/users/me"),
        updateMe: (payload) => apiClient.patch("/users/me", payload),
        getById: (id) => apiClient.get(`/users/${id}`),
        requestProStatus: (documents) => apiClient.post("/users/me/pro-status", { documents }),
        submitKyc: (documents) => apiClient.post("/users/me/kyc", { documents }),
        getProRequest: () => apiClient.get("/users/me/pro-request"),
        createProRequest: (reason) => apiClient.post("/users/me/pro-request", { reason }),
        getK6Request: () => apiClient.get("/users/me/k6-request"),
        createK6Request: (reason) => apiClient.post("/users/me/k6-request", { reason }),
        getAccessRequests: () => apiClient.get("/users/me/access-requests"),
        requestAccess: (moduleName, message) => apiClient.post("/users/me/access-requests", { moduleName, message }),
    };
    const postsApi = {
        list: (filters) => apiClient.get("/posts", { params: filters }),
        getById: (id) => apiClient.get(`/posts/${id}`),
        create: (payload) => apiClient.post("/posts", payload),
        update: (id, payload) => apiClient.patch(`/posts/${id}`, payload),
        publish: (id) => apiClient.patch(`/posts/${id}`, { status: "PUBLISHED" }),
        archive: (id) => apiClient.delete(`/posts/${id}`),
    };
    const applicationsApi = {
        apply: (postId, message) => apiClient.post("/applications", { postId, message }),
        getSent: () => apiClient.get("/applications/sent"),
        getReceived: (page = 1) => apiClient.get("/applications/received", { params: { page } }),
        getById: (id) => apiClient.get(`/applications/${id}`),
        updateStatus: (id, status) => apiClient.patch(`/applications/${id}`, { status }),
    };
    const chatsApi = {
        list: (page = 1) => apiClient.get("/chats", { params: { page } }),
        getMessages: (chatId, page = 1) => apiClient.get(`/chats/${chatId}/messages`, { params: { page } }),
        sendMessage: (chatId, text) => apiClient.post(`/chats/${chatId}/messages`, { text }),
        markRead: (chatId) => apiClient.patch(`/chats/${chatId}/read`),
        requestAdminHelp: (chatId) => apiClient.post(`/chats/${chatId}/admin-help`),
        /** Réservé à l'admin : rejoint une conversation via la route admin */
        adminJoin: (chatId) => apiClient.post(`/admin/chats/${chatId}/join`),
    };
    const documentsApi = {
        list: (filters) => apiClient.get("/documents", { params: filters }),
        getById: (id) => apiClient.get(`/documents/${id}`),
        create: (payload) => apiClient.post("/documents", payload),
        signRequest: (id, payload) => apiClient.post(`/documents/${id}/sign-request`, payload ?? {}),
        reject: (id, reason) => apiClient.post(`/documents/${id}/reject`, { reason }),
        cancel: (id) => apiClient.post(`/documents/${id}/cancel`),
        markPaid: (id) => apiClient.post(`/documents/${id}/mark-paid`),
    };
    const categoriesApi = {
        list: () => apiClient.get("/categories"),
    };
    const notificationsApi = {
        list: (page = 1) => apiClient.get("/notifications", { params: { page } }),
        markRead: (id) => apiClient.patch(`/notifications/${id}`),
        markAllRead: () => apiClient.post("/notifications/read-all"),
    };
    const favoritesApi = {
        list: () => apiClient.get("/favorites"),
        add: (postId) => apiClient.post("/favorites", { postId }),
        remove: (postId) => apiClient.delete(`/favorites/${postId}`),
    };
    const subscriptionsApi = {
        get: () => apiClient.get("/subscriptions"),
        getPlans: () => apiClient.get("/subscriptions/plans"),
        checkout: (plan) => apiClient.post("/subscriptions/checkout", { plan }),
    };
    const pushApi = {
        register: (token) => apiClient.post("/push/register", { token }),
        unregister: () => apiClient.delete("/push/register"),
    };
    const uploadApi = {
        uploadFile: (formData) => apiClient.post("/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
        uploadKyc: (formData) => apiClient.post("/upload/kyc", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    };
    const artisansApi = {
        /** Route publique — pas de token requis */
        list: (filters) => apiClient.get("/artisans", { params: filters }),
        /** Route publique — one page complète */
        getById: (id) => apiClient.get(`/artisans/${id}`),
        /** Enregistre une vue (rate-limitée côté serveur) */
        recordView: (id) => apiClient.post(`/artisans/${id}/view`),
    };
    const portfolioApi = {
        getMyPortfolio: () => apiClient.get("/users/me/portfolio"),
        updateMyPortfolio: (payload) => apiClient.patch("/users/me/portfolio", {
            portfolioData: payload,
        }),
        uploadPhoto: (formData) => apiClient.post("/users/me/portfolio/photos", formData, { headers: { "Content-Type": "multipart/form-data" } }),
        deletePhoto: (photoId) => apiClient.delete(`/users/me/portfolio/photos/${photoId}`),
        reorderPhotos: (order) => apiClient.patch("/users/me/portfolio/photos/reorder", { order }),
    };
    const adminApi = {
        getStats: () => apiClient.get("/admin/stats"),
    };
    const emergencyApi = {
        list: (filters) => apiClient.get("/emergency", { params: filters }),
        getById: (id) => apiClient.get(`/emergency/${id}`),
        create: (payload) => apiClient.post("/emergency", payload),
        accept: (id) => apiClient.post(`/emergency/${id}/accept`),
        cancel: (id, reason) => apiClient.post(`/emergency/${id}/cancel`, { reason }),
        resolve: (id) => apiClient.post(`/emergency/${id}/resolve`),
    };
    const appointmentsApi = {
        list: (filters) => apiClient.get("/appointments", { params: filters }),
        getById: (id) => apiClient.get(`/appointments/${id}`),
        create: (payload) => apiClient.post("/appointments", payload),
        confirm: (id) => apiClient.post(`/appointments/${id}/confirm`),
        decline: (id) => apiClient.post(`/appointments/${id}/decline`),
        cancel: (id) => apiClient.post(`/appointments/${id}/cancel`),
        complete: (id) => apiClient.post(`/appointments/${id}/complete`),
    };
    // ─── ALE : mobilité, livraison & commerces de proximité ─────────────────
    const ridesApi = {
        list: (filters) => apiClient.get("/ale/rides", { params: filters }),
        available: (page = 1) => apiClient.get("/ale/rides/available", { params: { page } }),
        getById: (id) => apiClient.get(`/ale/rides/${id}`),
        create: (payload) => apiClient.post("/ale/rides", payload),
        action: (id, action, reason) => apiClient.post(`/ale/rides/${id}/action`, { action, reason }),
    };
    const restaurantsApi = {
        /** Annuaire public — pas de token requis */
        list: (filters) => apiClient.get("/ale/restaurants", { params: filters }),
        getById: (id) => apiClient.get(`/ale/restaurants/${id}`),
        getMine: () => apiClient.get("/ale/restaurants/me"),
        updateMine: (payload) => apiClient.patch("/ale/restaurants/me", payload),
        listMyMenuItems: () => apiClient.get("/ale/restaurants/me/menu-items"),
        createMenuItem: (payload) => apiClient.post("/ale/restaurants/me/menu-items", payload),
        updateMenuItem: (id, payload) => apiClient.patch(`/ale/restaurants/me/menu-items/${id}`, payload),
        deleteMenuItem: (id) => apiClient.delete(`/ale/restaurants/me/menu-items/${id}`),
        listMyOrders: (status, page = 1) => apiClient.get("/ale/restaurants/me/orders", { params: { status, page } }),
    };
    const foodOrdersApi = {
        list: (filters) => apiClient.get("/ale/food-orders", { params: filters }),
        available: (page = 1) => apiClient.get("/ale/food-orders/available", { params: { page } }),
        getById: (id) => apiClient.get(`/ale/food-orders/${id}`),
        create: (payload) => apiClient.post("/ale/food-orders", payload),
        action: (id, action, reason) => apiClient.post(`/ale/food-orders/${id}/action`, { action, reason }),
    };
    const commercesApi = {
        /** Annuaire public — pas de token requis */
        list: (filters) => apiClient.get("/ale/commerces", { params: filters }),
        getById: (id) => apiClient.get(`/ale/commerces/${id}`),
        getMine: () => apiClient.get("/ale/commerces/me"),
        updateMine: (payload) => apiClient.patch("/ale/commerces/me", payload),
        listMyProducts: () => apiClient.get("/ale/commerces/me/products"),
        createProduct: (payload) => apiClient.post("/ale/commerces/me/products", payload),
        updateProduct: (id, payload) => apiClient.patch(`/ale/commerces/me/products/${id}`, payload),
        deleteProduct: (id) => apiClient.delete(`/ale/commerces/me/products/${id}`),
        listMyOrders: (status, page = 1) => apiClient.get("/ale/commerces/me/orders", { params: { status, page } }),
    };
    const commerceOrdersApi = {
        list: (filters) => apiClient.get("/ale/commerce-orders", { params: filters }),
        available: (page = 1) => apiClient.get("/ale/commerce-orders/available", { params: { page } }),
        getById: (id) => apiClient.get(`/ale/commerce-orders/${id}`),
        create: (payload) => apiClient.post("/ale/commerce-orders", payload),
        action: (id, action, reason) => apiClient.post(`/ale/commerce-orders/${id}/action`, { action, reason }),
    };
    const reviewsApi = {
        listForTarget: (targetUserId) => apiClient.get("/ale/reviews", {
            params: { targetUserId },
        }),
        create: (payload) => apiClient.post("/ale/reviews", payload),
    };
    const professionalsApi = {
        onboard: (payload) => apiClient.post("/ale/professionals/onboard", payload),
        getMe: () => apiClient.get("/ale/professionals/me"),
        setAvailability: (isAvailable, currentLat, currentLng) => apiClient.patch("/ale/professionals/me", { isAvailable, currentLat, currentLng }),
    };
    const aleAdminApi = {
        listPendingProfessionals: (status = "PENDING", page = 1) => apiClient.get("/ale/admin/professionals", { params: { status, page } }),
        decideProfessional: (userId, decision, note) => apiClient.post(`/ale/admin/professionals/${userId}`, { decision, note }),
        getStats: () => apiClient.get("/ale/admin/stats"),
    };
    return {
        authApi,
        usersApi,
        postsApi,
        applicationsApi,
        chatsApi,
        documentsApi,
        categoriesApi,
        notificationsApi,
        favoritesApi,
        subscriptionsApi,
        pushApi,
        uploadApi,
        artisansApi,
        portfolioApi,
        adminApi,
        emergencyApi,
        appointmentsApi,
        ridesApi,
        restaurantsApi,
        foodOrdersApi,
        commercesApi,
        commerceOrdersApi,
        reviewsApi,
        professionalsApi,
        aleAdminApi,
    };
}
